import { Component, ElementRef, Input, NgZone, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { NgbModal, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  BrandedReportsHelpModalComponent
} from '@shared/branded-reports/branded-reports-help-modal/branded-reports-help-modal.component';
import { ButtonComponent } from '@shared/button/button.component';
import { PrintableReportHeadingComponent } from '@shared/printable-report-heading/printable-report-heading.component';
import { BorderStyles } from './bar-colors.enum';
import { ZbChartBar, ZbChartData } from './zbchart-report.model';

@Component({
  selector: 'zbp-zbchart-report-table',
  imports: [
    ButtonComponent,
    PrintableReportHeadingComponent,
    NgbPagination,
    TranslateModule
  ],
  templateUrl: './zbchart-report-table.component.html',
  styleUrl: './zbchart-report-table.component.scss',
})
export class ZbChartReportTableComponent implements OnChanges {

  protected readonly helpMenuIconName = BrandedReportsHelpModalComponent.iconName;
  protected readonly BorderStyles = BorderStyles;
  protected readonly Math = Math;

  @Input() showProgressReportsHelp: boolean = false;
  @Input({ required: true }) reportLabel: string;
  @Input() leftHeaderText: string = '';
  @Input() rightHeaderText: string = '';
  @Input() zbChart: ZbChartData;
  @Input() isPrinting: boolean;
  @Input() printableReportHeadings: IPrintableReportHeading[];
  @Input() useFrontEndPagination = false;
  @Input() pageSize = 25;

  @ViewChild('floatingTooltip') floatingTooltip: ElementRef;

  page = 1;
  collectionSize: number;
  protected barsToDisplay: ZbChartBar[] = [];

  tooltipText: SafeHtml;
  disabledStatuses = [];
  /** If ChartBar was interacted with by Keyboard. */
  private isKeyboardEventOnChartBar = false;
  isHoveringBar = false;
  isHoveringTooltip = false;

  constructor(
    private modalService: NgbModal,
    private ngZone: NgZone,
  ) {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', (event: MouseEvent) => {
        // Align tooltip with pointer when it's not triggered by a keyboard event.
        if (!this.isKeyboardEventOnChartBar && this.tooltipText && !this.isHoveringTooltip) {
          const element = this.floatingTooltip.nativeElement;
          const tooltipRect = element.getBoundingClientRect();
          const { x, y } = this.getConstrainedTooltipPosition(
            event.clientX,
            event.clientY - 20,
            tooltipRect.width,
            tooltipRect.height
          );
          element.style.transform = `translate(${x}px, ${y}px)`;
        }
      });

      document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape' && this.tooltipText) {
          this.ngZone.run(() => {
            this.closeTooltip();
          });
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.zbChart) {
      this.page = 1;
      this.collectionSize = changes.zbChart.currentValue?.bars?.length || 0;
      this.disabledStatuses = [];
      this.paginateBars();
    }
    // TODO: Handle frontend pagination and printing when that becomes a feature
  }

  pageChangeWrapper(event: number) {
    this.page = event;
    this.paginateBars();
    window.scrollTo(0, 0);
  }

  paginateBars() {
    if (this.useFrontEndPagination && this.collectionSize > this.pageSize) {
      const start = (this.page - 1) * this.pageSize;
      const end = this.page * this.pageSize;
      this.barsToDisplay = this.zbChart.bars.slice(start, end);
    } else {
      this.barsToDisplay = this.zbChart?.bars ?? [];
    }
  }

  handleLegendClick(legendValue: string): void {
    const index = this.disabledStatuses.findIndex(v => v === legendValue);
    if (index > -1) {
      this.disabledStatuses.splice(index, 1);
    } else {
      this.disabledStatuses.push(legendValue);
    }
  }

  isDisabledStatus(id: string | undefined): boolean {
    return this.disabledStatuses.includes(id);
  }

  /** Helps position the tooltip within the viewport to prevent tooltip from being visually cut off. */
  private getConstrainedTooltipPosition(
    desiredX: number,
    desiredY: number,
    tooltipWidth: number,
    tooltipHeight: number
  ): { x: number; y: number } {
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const padding = 16;

    let x = desiredX;
    let y = desiredY;

    // Fix horizontal position if tooltip is off screen
    if (x < padding) {
      x = padding;
    } else if (x + tooltipWidth > viewportWidth - padding) {
      x = viewportWidth - tooltipWidth - padding;
    }

    // Fix vertical position if tooltip is off screen
    if (y < padding) {
      y = padding;
    } else if (y + tooltipHeight > viewportHeight - padding) {
      y = viewportHeight - tooltipHeight - padding;
    }

    return { x, y };
  }

  /** Hoer enter shows tooltip aligned with pointer. */
  hoverEnter(data: ZbChartBar, event: MouseEvent) {
    this.isKeyboardEventOnChartBar = false;
    this.tooltipText = data.tooltip;
    this.isHoveringBar = true;

    const element = this.floatingTooltip.nativeElement;

    setTimeout(() => {
      const tooltipRect = element.getBoundingClientRect();
      const { x, y } = this.getConstrainedTooltipPosition(
        event.clientX,
        event.clientY - 20,
        tooltipRect.width,
        tooltipRect.height
      );

      element.style.transform = `translate(${x}px, ${y}px)`;
    }, 0);
  }

  hoverLeave() {
    this.isHoveringBar = false;

    setTimeout(() => {
      if (!this.isHoveringTooltip && !this.isHoveringBar) {
        this.closeTooltip();
      }
    }, 0);
  }

  onTooltipEnter() {
    this.isHoveringTooltip = true;
  }

  onTooltipLeave() {
    this.isHoveringTooltip = false;

    if (!this.isHoveringBar) {
      this.closeTooltip();
    }
  }

  /** Focus enter shows tooltip aligned with with the chart's bar. */
  focusEnter(data: ZbChartBar, event: FocusEvent) {
    this.isKeyboardEventOnChartBar = true;
    this.tooltipText = data.tooltip;

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const element = this.floatingTooltip.nativeElement;

    setTimeout(() => {
      const tooltipRect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate initial position (centered above the element)
      const desiredX = centerX - tooltipRect.width / 2;
      const desiredY = centerY - 40;

      const { x, y } = this.getConstrainedTooltipPosition(
        desiredX,
        desiredY,
        tooltipRect.width,
        tooltipRect.height
      );

      element.style.transform = `translate(${x}px, ${y}px)`;
    }, 0);
  }

  focusLeave() {
    this.closeTooltip();
  }

  closeTooltip() {
    this.tooltipText = null;
    this.isHoveringBar = false;
    this.isHoveringTooltip = false;
    this.isKeyboardEventOnChartBar = false;
  }

  onReportHelpButtonClick() {
    BrandedReportsHelpModalComponent.open(this.modalService);
  }
}
